import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';

interface Font {
  id: number;
  name: string;
  font_file: string;
  font_family: string;
  is_premium: boolean;
  preview_text: string;
}

interface Color {
  id: number;
  name: string;
  hex_code: string;
  rgb_code: string;
  is_premium: boolean;
  category: string;
}

interface DesignTemplate {
  id: number;
  name: string;
  description: string;
  template_type: string;
  design_data: any;
  preview_image: string;
  is_free: boolean;
  price: string;
  is_featured: boolean;
  usage_count: number;
  created_at: string;
}

interface DesignElement {
  id: number;
  name: string;
  element_type: string;
  file: string;
  svg_data: string;
  category: string;
  tags: string;
  is_free: boolean;
  usage_count: number;
}

interface UserDesign {
  id: number;
  user: number;
  user_name: string;
  name: string;
  design_data: any;
  preview_image: string;
  product: number | null;
  is_public: boolean;
  allow_downloads: boolean;
  created_at: string;
  updated_at: string;
}

interface DesignState {
  fonts: Font[];
  colors: Color[];
  templates: DesignTemplate[];
  elements: DesignElement[];
  userDesigns: UserDesign[];
  currentDesign: UserDesign | null;
  loading: boolean;
  error: string | null;
}

const initialState: DesignState = {
  fonts: [],
  colors: [],
  templates: [],
  elements: [],
  userDesigns: [],
  currentDesign: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchFonts = createAsyncThunk(
  'design/fetchFonts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/design-editor/fonts/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Betűtípusok betöltése sikertelen');
    }
  }
);

export const fetchColors = createAsyncThunk(
  'design/fetchColors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/design-editor/colors/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Színek betöltése sikertelen');
    }
  }
);

export const fetchTemplates = createAsyncThunk(
  'design/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/design-editor/templates/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Sablonok betöltése sikertelen');
    }
  }
);

export const fetchElements = createAsyncThunk(
  'design/fetchElements',
  async (filters: { element_type?: string; category?: string } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.element_type) params.append('element_type', filters.element_type);
      if (filters.category) params.append('category', filters.category);

      const response = await api.get(`/design-editor/elements/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Elemek betöltése sikertelen');
    }
  }
);

export const fetchUserDesigns = createAsyncThunk(
  'design/fetchUserDesigns',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/design-editor/designs/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Designok betöltése sikertelen');
    }
  }
);

export const createUserDesign = createAsyncThunk(
  'design/createUserDesign',
  async (designData: { name: string; design_data: any; product?: number; preview_image?: File }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('name', designData.name);
      formData.append('design_data', JSON.stringify(designData.design_data));
      if (designData.product) {
        formData.append('product', designData.product.toString());
      }
      if (designData.preview_image) {
        formData.append('preview_image', designData.preview_image);
      }

      const response = await api.post('/design-editor/designs/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Design mentése sikertelen');
    }
  }
);

export const updateUserDesign = createAsyncThunk(
  'design/updateUserDesign',
  async ({ id, designData }: { id: number; designData: Partial<UserDesign> }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      if (designData.name) formData.append('name', designData.name);
      if (designData.design_data) formData.append('design_data', JSON.stringify(designData.design_data));
      if (designData.product) formData.append('product', designData.product.toString());

      const response = await api.patch(`/design-editor/designs/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Design frissítése sikertelen');
    }
  }
);

export const deleteUserDesign = createAsyncThunk(
  'design/deleteUserDesign',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/design-editor/designs/${id}/`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Design törlése sikertelen');
    }
  }
);

export const duplicateUserDesign = createAsyncThunk(
  'design/duplicateUserDesign',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/design-editor/designs/${id}/duplicate/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Design másolása sikertelen');
    }
  }
);

const designSlice = createSlice({
  name: 'design',
  initialState,
  reducers: {
    setCurrentDesign: (state, action: PayloadAction<UserDesign | null>) => {
      state.currentDesign = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Fonts
    builder.addCase(fetchFonts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchFonts.fulfilled, (state, action) => {
      state.loading = false;
      state.fonts = action.payload;
    });
    builder.addCase(fetchFonts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Colors
    builder.addCase(fetchColors.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchColors.fulfilled, (state, action) => {
      state.loading = false;
      state.colors = action.payload;
    });
    builder.addCase(fetchColors.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Templates
    builder.addCase(fetchTemplates.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTemplates.fulfilled, (state, action) => {
      state.loading = false;
      state.templates = action.payload;
    });
    builder.addCase(fetchTemplates.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Elements
    builder.addCase(fetchElements.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchElements.fulfilled, (state, action) => {
      state.loading = false;
      state.elements = action.payload;
    });
    builder.addCase(fetchElements.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch User Designs
    builder.addCase(fetchUserDesigns.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserDesigns.fulfilled, (state, action) => {
      state.loading = false;
      state.userDesigns = action.payload;
    });
    builder.addCase(fetchUserDesigns.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create User Design
    builder.addCase(createUserDesign.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createUserDesign.fulfilled, (state, action) => {
      state.loading = false;
      state.userDesigns.unshift(action.payload);
      state.currentDesign = action.payload;
    });
    builder.addCase(createUserDesign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update User Design
    builder.addCase(updateUserDesign.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUserDesign.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.userDesigns.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.userDesigns[index] = action.payload;
      }
      if (state.currentDesign?.id === action.payload.id) {
        state.currentDesign = action.payload;
      }
    });
    builder.addCase(updateUserDesign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete User Design
    builder.addCase(deleteUserDesign.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteUserDesign.fulfilled, (state, action) => {
      state.loading = false;
      state.userDesigns = state.userDesigns.filter((d) => d.id !== action.payload);
      if (state.currentDesign?.id === action.payload) {
        state.currentDesign = null;
      }
    });
    builder.addCase(deleteUserDesign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Duplicate User Design
    builder.addCase(duplicateUserDesign.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(duplicateUserDesign.fulfilled, (state, action) => {
      state.loading = false;
      state.userDesigns.unshift(action.payload);
    });
    builder.addCase(duplicateUserDesign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setCurrentDesign, clearError } = designSlice.actions;
export default designSlice.reducer;
